import { useState, useEffect } from 'react'
import { adventuresApi } from '@api/endpoints/adventures'
import {
  SavedSearchFilterDto,
  CreateSavedSearchFilterRequest,
} from '@api/types/adventure'

export function useSavedFilters() {
  const [filters, setFilters] = useState<SavedSearchFilterDto[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Load all saved filters
  const loadFilters = async () => {
    setIsLoading(true)
    setError(null)
    try {
      const response = await adventuresApi.getSavedFilters()
      setFilters(response)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load filters')
    } finally {
      setIsLoading(false)
    }
  }

  // Create a new saved filter
  const createFilter = async (data: CreateSavedSearchFilterRequest) => {
    setIsLoading(true)
    setError(null)
    try {
      const newFilter = await adventuresApi.createSavedFilter(data)
      setFilters((prev) => [...prev, newFilter])
      return newFilter
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create filter')
      throw err
    } finally {
      setIsLoading(false)
    }
  }

  // Update an existing saved filter
  const updateFilter = async (id: string, data: CreateSavedSearchFilterRequest) => {
    setIsLoading(true)
    setError(null)
    try {
      const updatedFilter = await adventuresApi.updateSavedFilter(id, data)
      setFilters((prev) =>
        prev.map((f) => (f.id === id ? updatedFilter : f))
      )
      return updatedFilter
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update filter')
      throw err
    } finally {
      setIsLoading(false)
    }
  }

  // Delete a saved filter
  const deleteFilter = async (id: string) => {
    setIsLoading(true)
    setError(null)
    try {
      await adventuresApi.deleteSavedFilter(id)
      setFilters((prev) => prev.filter((f) => f.id !== id))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete filter')
      throw err
    } finally {
      setIsLoading(false)
    }
  }

  // Set a filter as default
  const setDefaultFilter = async (id: string) => {
    setIsLoading(true)
    setError(null)
    try {
      await adventuresApi.setDefaultFilter(id)
      // Update local state to mark this filter as default and others as not
      setFilters((prev) =>
        prev.map((f) => ({ ...f, isDefault: f.id === id }))
      )
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to set default filter')
      throw err
    } finally {
      setIsLoading(false)
    }
  }

  // Get the default filter
  const getDefaultFilter = () => {
    return filters.find((f) => f.isDefault)
  }

  // Load filters on mount
  useEffect(() => {
    loadFilters()
  }, [])

  return {
    filters,
    isLoading,
    error,
    loadFilters,
    createFilter,
    updateFilter,
    deleteFilter,
    setDefaultFilter,
    getDefaultFilter,
  }
}
